// pages/hauler/JobDetailScreen.js
// Hauler-facing job detail screen with lifecycle actions.
//
// Lifecycle transitions flow exclusively through driverJobService:
//   available  → claim       → claimed
//   claimed    → release     → available
//   claimed    → markEnRoute → en_route
//   en_route   → markArrived → in_progress
//
// Components do NOT mutate Supabase directly. The state-machine trigger
// (migration 20260427000002) is the canonical source of truth.
//
// Verification block + completion modal land in Step 5; this file
// renders a "Go to verification" CTA when status === in_progress.
//
// Bucket A+B scope: no surge, no viability floor, no band, no
// snapshot-lock copy. LEU numerics are never shown.

import React, { useEffect, useRef, useState } from "react";
import { useJobEconomics } from "../../hooks/useJobEconomics";
import { getHaulerPayDisclosure, haulerScopeLabel } from "../../utils/haulerPayDisclosure";
import {
  claimJob,
  releaseJob,
  markEnRoute,
  markArrived,
  getBookingAddons,
} from "../../services/driverJobService";
import { LOAD_TIERS } from "../../constants";
import { capturePhoto, CAPTURE_ERRORS } from "../../services/captureOrchestrator";
import { normalizeVehicleProfile } from "../../dispatch/truckCapacityPolicy";
import StatusPill from "../../components/hauler/StatusPill";
import GateNotice from "../../components/hauler/GateNotice";
import HaulerToast from "../../components/hauler/HaulerToast";
import VerificationBlock from "../../components/hauler/VerificationBlock";
import CompletionModal from "../../components/hauler/CompletionModal";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
  red: "var(--hl-red)",
};

const PROTECTED_CLAIM_REASONS = new Set([
  "tier_not_allowed",
  "exceeds_rig_capacity",
  "trailer_suppressed",
  "missing_crew_names",
  "vehicle_ineligible",
]);

const GENERIC_CLAIM_REJECTION = "This job cannot be claimed right now. Choose another available offer or try again later.";

// Server-rejection message map mirrors DriverScreen.js handleClaimJob for
// non-protected account and workflow states only.
const CLAIM_REASON_COPY = {
  missing_driver_profile: "Your hauler profile is incomplete.",
  driver_not_approved: "Your hauler profile is not approved yet.",
  driver_unavailable: "Turn availability on before claiming jobs.",
  base_access_required: "Base access is required for this job.",
  unstarted_claim_limit: "Finish, start, or release your current claimed job before claiming another.",
};

function claimReasonCopy(reason) {
  if (PROTECTED_CLAIM_REASONS.has(reason)) return GENERIC_CLAIM_REJECTION;
  return CLAIM_REASON_COPY[reason] || "This job cannot be claimed right now.";
}

function tierItemsCopy(sizeKey) {
  const tier = LOAD_TIERS.find(t => t.key === sizeKey);
  return tier?.items || "Items list pending.";
}

function isBaggedTrashJob(job) {
  return !!(job?.type && String(job.type).toLowerCase().includes("bagged trash"));
}

export default function JobDetailScreen({ job, session, driverProfile, onBack, onChange }) {
  const econ = useJobEconomics(job);
  const userId = session?.user?.id || null;

  const [busy, setBusy] = useState(null); // 'claim' | 'release' | 'enroute' | 'arrived'
  const [toast, setToast] = useState(null);
  const [crewInput, setCrewInput] = useState("");

  // Verification state (Step 5) — only relevant when status === in_progress
  const [verifiedTier, setVerifiedTier] = useState(null);
  const [staging, setStaging] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Cluster A additions:
  const [bagCount, setBagCount] = useState("");
  // Live-camera modal state (mirrors DriverScreen pattern)
  const [modalStream, setModalStream] = useState(null);
  const [captureErr, setCaptureErr] = useState(null);
  const modalResolveRef = useRef(null);
  const modalVideoRef = useRef(null);

  const vehicleProfile = driverProfile ? normalizeVehicleProfile(driverProfile) : null;
  const isBagJob = isBaggedTrashJob(job);

  // Seed staging from job placement / notes when job changes (Gap 5).
  useEffect(() => {
    if (!job) return;
    const seed = job.placement_location || job.notes || "";
    setStaging(prev => prev || seed);
  }, [job?.id]); // eslint-disable-line

  // Customer add-ons declared before the pickup-day cutoff. Read-only;
  // verification on site remains the source of truth for size and pay.
  const [addons, setAddons] = useState([]);
  useEffect(() => {
    let alive = true;
    if (job?.id) getBookingAddons(job.id).then(rows => { if (alive) setAddons(rows); });
    else setAddons([]);
    return () => { alive = false; };
  }, [job?.id]);

  // Stream binding for the live-camera modal.
  useEffect(() => {
    const v = modalVideoRef.current;
    if (v && modalStream) {
      v.srcObject = modalStream;
      v.play().catch(() => {});
    }
  }, [modalStream]);

  function handlePhotoChange(file) {
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(file ? URL.createObjectURL(file) : null);
  }

  // Camera modal — receives the live stream from captureOrchestrator
  // and exposes Capture / Cancel buttons. Mirrors DriverScreen 497-515.
  function openMediaModal({ stream, onCapture, onCancel }) {
    setModalStream(stream);
    modalResolveRef.current = { onCapture, onCancel };
  }
  function handleShutter() {
    const video = modalVideoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob && modalResolveRef.current) modalResolveRef.current.onCapture(blob);
      setModalStream(null);
      modalResolveRef.current = null;
    }, "image/jpeg", 0.85);
  }
  function handleCancelCapture() {
    if (modalResolveRef.current) modalResolveRef.current.onCancel();
    setModalStream(null);
    modalResolveRef.current = null;
  }

  // Live-only capture flow. captureOrchestrator does Capacitor →
  // getUserMedia → native (capture="environment" file input). The
  // chain is enforced by the orchestrator — gallery uploads are never
  // permitted in this code path (only the legacy backup_upload path
  // fires when ALL live-camera methods fail, and we surface that as
  // an error to the Hauler).
  async function triggerLiveCapture() {
    setCaptureErr(null);
    const result = await capturePhoto(1, { showMediaModal: openMediaModal });
    if (!result.ok) {
      const msg = CAPTURE_ERRORS[result.errorCode] || "Camera error";
      setCaptureErr(msg);
      showToast(msg, "err");
      return;
    }
    handlePhotoChange(result.file);
  }

  const showToast = (message, kind = "ok") => setToast({ message, kind });

  if (!job || !econ) {
    return (
      <div style={{ padding: 24, color: COLORS.text }}>
        <button onClick={onBack} style={backBtn}>‹ Back</button>
        <div style={{ marginTop: 16, fontSize: 13, color: COLORS.sub }}>Job no longer available.</div>
      </div>
    );
  }

  const status = job.status || "available";
  const isMine = job.assigned_driver === userId;
  const disclosure = getHaulerPayDisclosure(job, econ);
  const showExactDisclosure = disclosure.showExactBreakdown;
  const scopeLabel = haulerScopeLabel(job);
  const crewProvided = crewInput
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);

  // ── Lifecycle handlers ────────────────────────────────────
  async function doClaim() {
    if (!userId) { showToast("Sign in required.", "err"); return; }
    setBusy("claim");
    try {
      await claimJob({ requestId: job.id, driverUserId: userId, crewNames: crewProvided });
      showToast("Job claimed.", "ok");
      onChange && onChange("claimed");
    } catch (e) {
      const msg = e?.message || "";
      if (msg === "JOB_ALREADY_CLAIMED") {
        showToast("Already claimed by another hauler.", "warn");
      } else if (msg.startsWith("CLAIM_REJECTED:")) {
        const reason = msg.split(":")[1];
        showToast(claimReasonCopy(reason), "err");
      } else {
        showToast("Claim failed: " + msg, "err");
      }
    }
    setBusy(null);
  }

  async function doRelease() {
    if (!userId) return;
    setBusy("release");
    try {
      await releaseJob({ requestId: job.id, driverUserId: userId });
      showToast("Released back to pool.", "ok");
      onChange && onChange("available");
    } catch (e) {
      showToast("Release failed: " + (e?.message || ""), "err");
    }
    setBusy(null);
  }

  async function doMarkEnRoute() {
    if (!userId) return;
    setBusy("enroute");
    try {
      await markEnRoute({ requestId: job.id, driverUserId: userId });
      showToast("Customer notified — you're on the way.", "ok");
      onChange && onChange("en_route");
    } catch (e) {
      showToast("Failed: " + (e?.message || ""), "err");
    }
    setBusy(null);
  }

  async function doMarkArrived() {
    if (!userId) return;
    setBusy("arrived");
    try {
      await markArrived({ requestId: job.id, driverUserId: userId });
      showToast("Arrived — job in progress.", "ok");
      onChange && onChange("in_progress");
    } catch (e) {
      showToast("Failed: " + (e?.message || ""), "err");
    }
    setBusy(null);
  }

  // ── Render ────────────────────────────────────────────────
  const isClaimable = status === "available";
  const showRelease = isMine && status === "claimed";
  const showEnRoute = isMine && status === "claimed";
  const showArrived = isMine && status === "en_route";
  const showVerify  = isMine && status === "in_progress";
  const verifyReady = !!verifiedTier && !!(staging && staging.trim()) && !!photoFile;

  return (
    <div style={{ padding: "16px 16px 120px", color: COLORS.text }}>
      <button onClick={onBack} style={backBtn}>‹ Back</button>

      {/* Status + tier header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, marginBottom: 4 }}>
        <StatusPill status={status} />
        <span style={{ fontSize: 11, color: COLORS.muted }}>Zone {job.zone || "—"}</span>
      </div>
      <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: COLORS.sub, textTransform: "uppercase", marginBottom: 12 }}>
        {showExactDisclosure ? econ.tierLabel : scopeLabel}
      </div>

      {/* Pay block */}
      <div style={cardStyle}>
        <div style={{ fontSize: 11, color: COLORS.sub, marginBottom: 6 }}>{disclosure.label}</div>
        <div style={{ fontSize: 32, fontWeight: 700, color: COLORS.green, letterSpacing: -0.5, lineHeight: 1 }}>
          {disclosure.payText}
        </div>
        <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 6 }}>
          {showExactDisclosure
            ? `~${econ.durationMin} min · ~$${econ.hourly.toFixed(0)}/hr · Final payout recorded`
            : disclosure.detailText}
        </div>
      </div>

      {/* Address */}
      <Section title="Address">
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {job.addr || "Address pending"}
        </div>
        {job.unit_number && (
          <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 2 }}>Unit {job.unit_number}</div>
        )}
      </Section>

      {/* Placement */}
      <Section title="Placement">
        <div style={{ fontSize: 13, color: COLORS.text }}>
          {job.staging_location || job.placement || "Driveway or outdoor placement area"}
        </div>
      </Section>

      {/* Items */}
      <Section title="Items">
        <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.5 }}>
          {showExactDisclosure
            ? tierItemsCopy(job.size)
            : "Verify the actual load at pickup using the customer photos, staging note, and what is present on-site."}
        </div>
        {addons.length > 0 && (
          <div style={{ marginTop: 10, background: "rgba(61,144,217,.10)", border: "1px solid rgba(61,144,217,.32)", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "#3D90D9", marginBottom: 6 }}>Added after booking · verify on site</div>
            {addons.map((a, i) => (
              <div key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: "#16233B" }}>
                • {a.description}{a.added_mattress_pieces > 0 ? ` (+${a.added_mattress_pieces} mattress piece${a.added_mattress_pieces > 1 ? "s" : ""})` : ""}
              </div>
            ))}
          </div>
        )}
        {job.notes && (
          <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 8, fontStyle: "italic" }}>
            Customer note: "{job.notes}"
          </div>
        )}
      </Section>

      {/* Gate guidance is uniform before verification so tier/capacity cannot be reconstructed. */}
      {isClaimable && (
        <GateNotice kind="Claim readiness">
          Review access, vehicle fit, and any support you plan to bring before accepting.
        </GateNotice>
      )}

      {/* Verification block (in_progress only) */}
      {showVerify && (
        <VerificationBlock
          verifiedTier={verifiedTier}
          onChangeVerifiedTier={setVerifiedTier}
          staging={staging}
          onChangeStaging={setStaging}
          photoFile={photoFile}
          photoPreviewUrl={photoPreviewUrl}
          onTriggerCapture={triggerLiveCapture}
          isBagJob={isBagJob}
          bagCount={bagCount}
          onChangeBagCount={setBagCount}
        />
      )}

      {/* Optional support input. It is not tier-derived before verification. */}
      {isClaimable && (
        <Section title="Support crew (optional)">
          <input
            type="text"
            value={crewInput}
            onChange={(e) => setCrewInput(e.target.value)}
            placeholder="First Last, First Last"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              background: "var(--hl-input-bg)",
              color: COLORS.text,
              fontFamily: "inherit",
              fontSize: 14,
              outline: "none",
            }}
          />
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
            Add names if you plan to bring help.
          </div>
        </Section>
      )}

      {/* Action bar */}
      <div style={actionBarStyle}>
        {isClaimable && (
          <button
            disabled={busy != null}
            onClick={doClaim}
            style={primaryBtn(busy === "claim")}
          >
            {busy === "claim" ? "Claiming…" : "Claim job"}
          </button>
        )}

        {showEnRoute && (
          <button
            disabled={busy != null}
            onClick={doMarkEnRoute}
            style={primaryBtn(busy === "enroute")}
          >
            {busy === "enroute" ? "Notifying…" : "On the way"}
          </button>
        )}

        {showArrived && (
          <button
            disabled={busy != null}
            onClick={doMarkArrived}
            style={primaryBtn(busy === "arrived")}
          >
            {busy === "arrived" ? "Updating…" : "Arrived at pickup"}
          </button>
        )}

        {showVerify && (
          <button
            disabled={!verifyReady}
            onClick={() => setShowCompletionModal(true)}
            style={{
              ...primaryBtn(false),
              background: verifyReady ? COLORS.green : "rgba(3,166,106,.4)",
              cursor: verifyReady ? "pointer" : "not-allowed",
            }}
          >
            {verifyReady ? "Confirm completion" : "Fill verification to continue"}
          </button>
        )}

        {showRelease && (
          <button
            disabled={busy != null}
            onClick={doRelease}
            style={destructiveBtn(busy === "release")}
          >
            {busy === "release" ? "Releasing…" : "Release job"}
          </button>
        )}
      </div>

      <HaulerToast message={toast?.message} kind={toast?.kind} onDismiss={() => setToast(null)} />

      {showCompletionModal && (
        <CompletionModal
          job={job}
          session={session}
          verifiedTier={verifiedTier}
          staging={staging}
          photoFile={photoFile}
          bagCount={bagCount}
          isBagJob={isBagJob}
          vehicleProfile={vehicleProfile}
          addons={addons}
          onCancel={() => setShowCompletionModal(false)}
          onCompleted={() => {
            setShowCompletionModal(false);
            showToast("Job completed.", "ok");
            onChange && onChange("complete");
          }}
        />
      )}

      {/* Live-camera modal (mirrors DriverScreen 1538-1545). */}
      {modalStream && (
        <div style={{
          position: "fixed",
          inset: 0,
          zIndex: 300,
          background: "#000",
          display: "flex",
          flexDirection: "column",
        }}>
          <video
            ref={modalVideoRef}
            playsInline
            muted
            style={{ flex: 1, width: "100%", objectFit: "cover" }}
          />
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            padding: "20px 16px calc(28px + env(safe-area-inset-bottom, 0px))",
            background: "rgba(0,0,0,.85)",
          }}>
            <button
              type="button"
              onClick={handleCancelCapture}
              style={{
                padding: "13px 28px",
                borderRadius: 12,
                border: "1.5px solid rgba(255,255,255,.3)",
                background: "transparent",
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                cursor: "pointer",
                fontFamily: "inherit",
                minHeight: 48,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleShutter}
              style={{
                padding: "13px 36px",
                borderRadius: 12,
                border: "none",
                background: "#fff",
                color: "#0A1628",
                fontWeight: 800,
                fontSize: 15,
                cursor: "pointer",
                fontFamily: "inherit",
                minHeight: 48,
              }}
            >
              Capture
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: COLORS.sub, textTransform: "uppercase", marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

const cardStyle = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: "14px 14px",
  marginBottom: 10,
};

const backBtn = {
  background: "transparent",
  border: "none",
  color: COLORS.sky,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 4px",
  fontFamily: "inherit",
  minHeight: 44,
};

const actionBarStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 16,
};

function primaryBtn(loading) {
  return {
    width: "100%",
    padding: "14px",
    borderRadius: 12,
    border: "none",
    background: loading ? "rgba(61,144,217,.5)" : COLORS.sky,
    color: "#FFFFFF",
    fontWeight: 800,
    fontSize: 15,
    cursor: loading ? "wait" : "pointer",
    fontFamily: "inherit",
    minHeight: 48,
  };
}

function destructiveBtn(loading) {
  return {
    width: "100%",
    padding: "12px",
    borderRadius: 12,
    border: `1px solid ${COLORS.red}`,
    background: "transparent",
    color: COLORS.red,
    fontWeight: 700,
    fontSize: 14,
    cursor: loading ? "wait" : "pointer",
    fontFamily: "inherit",
    minHeight: 44,
  };
}
